import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import FileViewer from 'react-file-viewer';
import axios from 'axios';

const DEFAULT_BUCKET = 'portfolio';

// Supported file types for react-file-viewer
const SUPPORTED_FILE_TYPES = new Set(['pdf','png','jpg','jpeg','gif','bmp','webp','csv','doc','docx','xls','xlsx','ppt','pptx','mp4','webm','mov','m4v','avi','mp3','wav','ogg']);

// Text file extensions
const TEXT_FILE_EXTENSIONS = ['txt', 'md', 'json', 'xml', 'html', 'css', 'js', 'jsx', 'ts', 'tsx', 'py', 'java', 'c', 'cpp', 'h', 'sh', 'yml', 'yaml'];

// Error categorization helper
const isGatewayError = (status) => {
  return status === 502 || status === 503 || status === 504 || (status >= 520 && status <= 527);
};

const categorizeError = (error, response = null) => {
  // Guard against invalid error objects
  if (!error || typeof error !== 'object') {
    return {
      type: 'unknown',
      message: 'An unknown error occurred',
      userMessage: '❌ Error'
    };
  }
  
  const errorMessage = error.message || '';
  
  // Check if it's a CORS error
  if (errorMessage && (
    errorMessage.includes('CORS') ||
    errorMessage.includes('NetworkError') ||
    errorMessage.includes('Failed to fetch') ||
    (error.name === 'TypeError' && errorMessage.includes('fetch'))
  )) {
    return {
      type: 'cors',
      message: 'Storage server is currently under maintenance. Please try again later.',
      userMessage: '🔧 Server Under Maintenance'
    };
  }
  
  // Check for Access Denied / Bucket doesn't exist
  const lowerMessage = errorMessage.toLowerCase();
  if (lowerMessage && (
    lowerMessage.includes('access denied') ||
    lowerMessage.includes('forbidden') ||
    (lowerMessage.includes('bucket') && lowerMessage.includes('not found')) ||
    (lowerMessage.includes('bucket') && lowerMessage.includes('does not exist')) ||
    lowerMessage.includes('no such bucket')
  )) {
    return {
      type: 'access_denied',
      message: 'The requested bucket does not exist or you do not have permission to access it.',
      userMessage: '🚫 Access Denied - Bucket Not Found'
    };
  }
  
  // Check for Cloudflare gateway errors (502, 503, 504, 520-527)
  if (response) {
    const status = response.status;
    if (isGatewayError(status)) {
      return {
        type: 'gateway_error',
        message: 'The storage service is currently offline. Please try again later.',
        userMessage: '⚠️ Service Offline'
      };
    }
  }
  
  // Check error message for gateway/cloudflare keywords
  if (lowerMessage && (
    lowerMessage.includes('gateway') ||
    lowerMessage.includes('bad gateway') ||
    lowerMessage.includes('service unavailable') ||
    lowerMessage.includes('gateway timeout') ||
    lowerMessage.includes('cloudflare')
  )) {
    return {
      type: 'gateway_error',
      message: 'The storage service is currently offline. Please try again later.',
      userMessage: '⚠️ Service Offline'
    };
  }
  
  // Default error
  return {
    type: 'unknown',
    message: errorMessage || 'An error occurred',
    userMessage: '❌ Error'
  };
};

function StorageBrowser() {
  const [bucketName, setBucketName] = useState(DEFAULT_BUCKET);
  const [prefix, setPrefix] = useState('');
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Preview state for react-file-viewer
  const [previewOpen, setPreviewOpen] = useState(false);
  const [viewerUrl, setViewerUrl] = useState('');
  const [viewerType, setViewerType] = useState(''); // extension like 'pdf', 'png', etc.
  const [viewerName, setViewerName] = useState('');
  const [textContent, setTextContent] = useState(''); // for text files
  // Progress tracking state
  const [uploadProgress, setUploadProgress] = useState(null); // { filename: string, progress: number }
  const [downloadProgress, setDownloadProgress] = useState(null); // { filename: string, progress: number }

  // Virtual (client-side only) directories storage key
  // Store only for current session (clears when tab is closed or session ends)
  const VDIRS_KEY = 'storageVirtualDirs';

  const loadVD = () => {
    try { return JSON.parse(sessionStorage.getItem(VDIRS_KEY) || '{}'); } catch { return {}; }
  };
  const saveVD = (data) => {
    try { sessionStorage.setItem(VDIRS_KEY, JSON.stringify(data)); } catch {
      // Silently fail if sessionStorage is unavailable
    }
  };
  const getVirtualDirs = (bucket, pref) => {
    const all = loadVD();
    return (all?.[bucket]?.[pref]) || [];
  };
  const addVirtualDir = (bucket, pref, name) => {
    const all = loadVD();
    if (!all[bucket]) all[bucket] = {};
    const existing = new Set(all[bucket][pref] || []);
    existing.add(name);
    all[bucket][pref] = Array.from(existing);
    saveVD(all);
  };
  const removeVirtualDir = (bucket, pref, name) => {
    const all = loadVD();
    if (!all[bucket] || !all[bucket][pref]) return;
    all[bucket][pref] = (all[bucket][pref] || []).filter(n => n !== name);
    if (all[bucket][pref].length === 0) delete all[bucket][pref];
    saveVD(all);
  };

  const userId = localStorage.getItem('userId');
  const token = localStorage.getItem('token');

  const fetchListing = async (nextPrefix = prefix) => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch('https://storage.ganamaga.me/api/storage', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'browse',
          userId,
          token,
          bucketName,
          prefix: nextPrefix
        })
      });
      
      // Check for Cloudflare gateway errors before parsing
      if (!res.ok && isGatewayError(res.status)) {
        const errorInfo = categorizeError(new Error(`Gateway error: ${res.status}`), res);
        throw new Error(errorInfo.message);
      }
      
      const data = await res.json();
      if (!data.success) {
        const errorInfo = categorizeError(new Error(data.message || 'Failed to browse'), res);
        throw new Error(errorInfo.message);
      }

      // Normalize incoming files and derive folders based on slash-delimited keys
      const incomingFiles = Array.isArray(data.files) ? data.files : [];
      const normFiles = incomingFiles.map((it) => {
        const key = (it && (it.key || it.name)) || '';
        const safeKey = String(key);
        const name = safeKey.split('/').pop();
        return { key: safeKey, name };
      }).filter(f => f.key);

      const pfx = (data.prefix ?? nextPrefix) || '';
      const directFiles = [];
      const folderSet = new Set();
      for (const f of normFiles) {
        if (!pfx || f.key.startsWith(pfx)) {
          const rest = f.key.slice(pfx.length);
          if (rest.includes('/')) {
            const folderName = rest.split('/')[0];
            if (folderName) folderSet.add(folderName);
          } else {
            // direct child of current prefix
            directFiles.push({ key: f.key, name: f.name });
          }
        }
      }

      // Merge derived folders with any server-reported folders, de-duplicated by name
      const derivedFolders = Array.from(folderSet).map(name => ({ name, prefix: (pfx || '') + name + '/' }));
      const serverFolders = Array.isArray(data.folders) ? data.folders : [];
      const mergedByName = new Map();
      for (const sf of serverFolders) {
        if (sf && sf.name) mergedByName.set(sf.name, { name: sf.name, prefix: sf.prefix || ((pfx || '') + sf.name + '/') });
      }
      for (const df of derivedFolders) {
        if (!mergedByName.has(df.name)) mergedByName.set(df.name, df);
      }

      // Merge in-memory session virtual dirs
      const vnames = getVirtualDirs(bucketName, pfx);
      for (const name of vnames) {
        if (!mergedByName.has(name)) {
          mergedByName.set(name, { name, prefix: (pfx || '') + name + '/' });
        } else {
          // Cleanup: if folder now exists from server/derived, remove it from virtual tracking
          removeVirtualDir(bucketName, pfx, name);
        }
      }

      setPrefix(pfx);
      setFolders(Array.from(mergedByName.values()).sort((a,b) => a.name.localeCompare(b.name)));
      setFiles(directFiles.sort((a,b) => a.name.localeCompare(b.name)));
    } catch (e) {
      const errorInfo = categorizeError(e);
      setError(errorInfo.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchListing('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bucketName]);

  const navigateTo = (subPrefix) => {
    const newPrefix = subPrefix;
    fetchListing(newPrefix);
  };

  const handleCreateFolder = () => {
    const input = window.prompt('New folder name');
    if (input == null) return;
    const name = input.trim();
    if (!name) {
      alert('Folder name cannot be empty');
      return;
    }
    if (/[/\\]/.test(name)) {
      alert('Folder name cannot contain slashes');
      return;
    }
    if (name === '.' || name === '..') {
      alert('Invalid folder name');
      return;
    }
    const existingNames = new Set((folders || []).map(f => (f.name || '').toLowerCase()));
    if (existingNames.has(name.toLowerCase())) {
      alert('A folder with this name already exists here');
      return;
    }
    addVirtualDir(bucketName, prefix, name);
    setFolders(prev => ([...(prev || []), { name, prefix: (prefix || '') + name + '/' }]));
    // Navigate into the newly created folder to enable immediate uploads
    navigateTo((prefix || '') + name + '/');
  };

  const goUp = () => {
    if (!prefix) return;
    const parts = prefix.replace(/\/$/, '').split('/');
    parts.pop();
    const parent = parts.length ? parts.join('/') + '/' : '';
    fetchListing(parent);
  };

  const handleDelete = async (key) => {
    if (!window.confirm(`Delete ${key}?`)) return;
    try {
      const res = await fetch('https://storage.ganamaga.me/api/storage', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'deleteFile', userId, token, bucketName, key })
    });
      
      // Check for Cloudflare gateway errors before parsing
      if (!res.ok && isGatewayError(res.status)) {
        const errorInfo = categorizeError(new Error(`Gateway error: ${res.status}`), res);
        throw new Error(errorInfo.message);
      }
      
      const data = await res.json();
      if (!data.success) {
        const errorInfo = categorizeError(new Error(data.message || 'Failed to delete'), res);
        throw new Error(errorInfo.message);
      }
      fetchListing();
    } catch (e) {
      const errorInfo = categorizeError(e);
      alert(errorInfo.message);
    }
  };

  const handleDownload = async (key) => {
    const filename = key.split('/').pop();
    setDownloadProgress({ filename, progress: 0 });
    
    try {
      const res = await fetch('https://storage.ganamaga.me/api/storage', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'downloadFile', userId, token, bucketName, key })
      });
      
      // Check for Cloudflare gateway errors
      if (!res.ok) {
        if (isGatewayError(res.status)) {
          const errorInfo = categorizeError(new Error(`Gateway error: ${res.status}`), res);
          throw new Error(errorInfo.message);
        }
        const data = await res.json();
        const errorInfo = categorizeError(new Error(data.message || 'Failed to download'), res);
        throw new Error(errorInfo.message);
      }
      
      const contentLength = res.headers.get('content-length');
      const total = parseInt(contentLength, 10);
      
      if (!contentLength || isNaN(total)) {
        // If content-length is not available, just download without progress
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        setDownloadProgress(null);
        return;
      }
      
      // Track download progress
      const reader = res.body.getReader();
      let receivedLength = 0;
      const chunks = [];
      
      while (true) {
        const { done, value } = await reader.read();
        
        if (done) break;
        
        chunks.push(value);
        receivedLength += value.length;
        
        const progress = Math.round((receivedLength / total) * 100);
        setDownloadProgress({ filename, progress });
      }
      
      // Create blob from chunks
      const blob = new Blob(chunks);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      
      setDownloadProgress(null);
    } catch (e) {
      setDownloadProgress(null);
      const errorInfo = categorizeError(e);
      alert(errorInfo.message);
    }
  };

  const handleOpen = async (key) => {
    try {
      if (!userId || !token) {
        throw new Error('Authentication required. Please log in again.');
      }
      
      const filename = key.split('/').pop();
      const ext = (filename.includes('.') ? filename.split('.').pop() : '').toLowerCase();
      
      // Use CDN proxy URL with authentication
      const filePath = `${bucketName}/${key}`;
      const directUrl = `https://cdn.ganamaga.me/sec_get/${filePath}?uid=${encodeURIComponent(userId)}&token=${encodeURIComponent(token)}`;

      const isTextLike = (extension) => {
        return TEXT_FILE_EXTENSIONS.includes(extension);
      };

      // If text-like, fetch content and show in modal (not via FileViewer)
      if (isTextLike(ext)) {
        const response = await fetch(directUrl);
        if (!response.ok) {
          if (isGatewayError(response.status)) {
            const errorInfo = categorizeError(new Error(`Gateway error: ${response.status}`), response);
            throw new Error(errorInfo.message);
          }
          const errorInfo = categorizeError(new Error(`Failed to fetch file content: ${response.status} ${response.statusText}`), response);
          throw new Error(errorInfo.message);
        }
        const content = await response.text();
        setTextContent(content);
        setViewerUrl('');
        setViewerType('txt');
        setViewerName(filename);
        setPreviewOpen(true);
        return;
      }

      if (SUPPORTED_FILE_TYPES.has(ext)) {
        setViewerUrl(directUrl);
        setViewerType(ext || 'pdf');
        setViewerName(filename);
        setTextContent('');
        setPreviewOpen(true);
        return;
      }

      // Fallback for unsupported types: trigger a download
      const a = document.createElement('a');
      a.href = directUrl;
      a.download = filename;
      a.target = '_blank';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      const errorInfo = categorizeError(e);
      alert(errorInfo.message);
    }
  };

  const handleUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    const CHUNK_SIZE = 90 * 1024 * 1024; // 90MB chunks per Cloudflare limits
    let firstError = null;
    try {
      for (const file of files) {
        setUploadProgress({ filename: file.name, progress: 0 });
        const rawKey = `${prefix || ''}${file.name}`;
        const cleanKey = rawKey.replace(/^\/+/, '');

        try {
          if (file.size > CHUNK_SIZE) {
            // Chunked upload
            const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
            const uploadId = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
            for (let i = 0; i < totalChunks; i++) {
              const start = i * CHUNK_SIZE;
              const end = Math.min(start + CHUNK_SIZE, file.size);
              const chunk = file.slice(start, end);
              const formData = new FormData();
              formData.append('action', 'uploadChunk');
              formData.append('userId', userId);
              formData.append('token', token);
              formData.append('bucketName', bucketName);
              formData.append('key', cleanKey);
              formData.append('uploadId', uploadId);
              formData.append('index', String(i));
              formData.append('totalChunks', String(totalChunks));
              formData.append('contentType', file.type || 'application/octet-stream');
              // Use field name 'file' to match server multer.single('file')
              formData.append('file', chunk, `${file.name}.part-${i}`);

              // Use axios with progress tracking for each chunk
              const response = await axios.post('https://storage.ganamaga.me/api/storage', formData, {
                onUploadProgress: (progressEvent) => {
                  // Check for valid total bytes to prevent division by zero
                  if (progressEvent.total > 0) {
                    // Calculate progress within this chunk
                    const chunkProgress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
                    // Calculate overall progress: completed chunks + current chunk progress
                    const overallProgress = Math.round(((i + (chunkProgress / 100)) / totalChunks) * 100);
                    setUploadProgress({ filename: file.name, progress: overallProgress });
                  }
                }
              });
              
              if (!response.data.success) {
                const errorInfo = categorizeError(new Error(response.data.message || `Chunk ${i + 1}/${totalChunks} upload failed for ${file.name}`));
                if (!firstError) firstError = new Error(errorInfo.message);
                throw firstError;
              }
            }
            if (!firstError) {
              // Complete upload using axios
              const response = await axios.post('https://storage.ganamaga.me/api/storage', {
                action: 'completeUpload',
                userId,
                token,
                bucketName,
                key: cleanKey,
                uploadId,
                totalChunks,
                contentType: file.type || 'application/octet-stream'
              }, {
                headers: { 'Content-Type': 'application/json' }
              });
              
              if (!response.data.success && !firstError) {
                const errorInfo = categorizeError(new Error(response.data.message || `Finalize failed for ${file.name}`));
                firstError = new Error(errorInfo.message);
                throw firstError;
              }
            }
          } else {
            // Small file: direct upload with progress tracking
            const formData = new FormData();
            formData.append('action', 'uploadFile');
            formData.append('userId', userId);
            formData.append('token', token);
            formData.append('bucketName', bucketName);
            formData.append('key', cleanKey);
            formData.append('file', file);

            // Use axios with progress tracking
            const response = await axios.post('https://storage.ganamaga.me/api/storage', formData, {
              onUploadProgress: (progressEvent) => {
                // Check for valid total bytes to prevent division by zero
                if (progressEvent.total > 0) {
                  const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
                  setUploadProgress({ filename: file.name, progress });
                }
              }
            });
            
            if (!response.data.success) {
              const errorInfo = categorizeError(new Error(response.data.message || `Upload failed for ${file.name}`));
              if (!firstError) firstError = new Error(errorInfo.message);
              throw firstError;
            }
          }
        } catch (fileError) {
          // Clear progress for this file if error occurs
          setUploadProgress(null);
          // Handle axios errors and preserve error information
          if (!firstError) {
            if (fileError.isAxiosError || fileError.response) {
              const errorInfo = categorizeError(fileError, fileError.response);
              firstError = new Error(errorInfo.message);
              // Preserve original error for debugging
              firstError.originalError = fileError;
            } else {
              firstError = fileError;
            }
          }
          // Continue to next file or break if needed
          break;
        }
      }
      // Refresh listing once after all uploads
      fetchListing();
      if (firstError) throw firstError;
    } catch (e) {
      const errorInfo = categorizeError(e);
      alert(errorInfo.message);
    } finally {
      // Clear progress and reset input value to allow re-uploading same filename(s)
      setUploadProgress(null);
      event.target.value = '';
    }
  };

  const closePreview = () => {
    // No need to revoke URL since we're using direct URLs now
    setPreviewOpen(false);
    setViewerUrl('');
    setViewerType('');
    setViewerName('');
    setTextContent('');
  };

  return (
    <Container>
      <Controls>
        <div>
          <label>
            Bucket:
            <input value={bucketName} onChange={(e) => setBucketName(e.target.value)} placeholder="bucket" />
          </label>
        </div>
        <div>
          <button onClick={goUp} disabled={!prefix}>Up</button>
          <button onClick={handleCreateFolder} style={{ marginLeft: 8 }}>New Folder</button>
          <span style={{ marginLeft: 8, opacity: 0.8 }}>Path: /{prefix}</span>
        </div>
        <div>
          <input type="file" multiple onChange={handleUpload} />
        </div>
      </Controls>

      {loading && <Info>Loading...</Info>}
      {error && <ErrorMsg>{error}</ErrorMsg>}
      
      {/* Upload Progress Bar */}
      {uploadProgress && (
        <ProgressContainer>
          <ProgressLabel>Uploading: {uploadProgress.filename}</ProgressLabel>
          <ProgressBarWrapper>
            <ProgressBarFill progress={uploadProgress.progress} />
          </ProgressBarWrapper>
          <ProgressPercentage>{uploadProgress.progress}%</ProgressPercentage>
        </ProgressContainer>
      )}
      
      {/* Download Progress Bar */}
      {downloadProgress && (
        <ProgressContainer>
          <ProgressLabel>Downloading: {downloadProgress.filename}</ProgressLabel>
          <ProgressBarWrapper>
            <ProgressBarFill progress={downloadProgress.progress} />
          </ProgressBarWrapper>
          <ProgressPercentage>{downloadProgress.progress}%</ProgressPercentage>
        </ProgressContainer>
      )}

      <List>
        {folders.map(f => (
          <Row key={f.prefix}>
            <span role="button" style={{ color: '#0a7', cursor: 'pointer' }} onClick={() => navigateTo(f.prefix)}>
              📁 {f.name}
            </span>
          </Row>
        ))}
        {files.map(file => (
          <Row key={file.key}>
            <span>📄 {file.name}</span>
            <Actions>
              <button onClick={() => handleOpen(file.key)}>Open</button>
              <button onClick={() => handleDownload(file.key)}>Download</button>
              <button onClick={() => handleDelete(file.key)} style={{ color: 'red' }}>Delete</button>
            </Actions>
          </Row>
        ))}
        {!folders.length && !files.length && !loading && <Info>Empty</Info>}
      </List>

      {previewOpen && (
        <ModalOverlay onClick={closePreview}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <strong>{viewerName}</strong>
              <button onClick={closePreview}>Close</button>
            </ModalHeader>
            <ModalBody>
              {textContent ? (
                <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{textContent}</pre>
              ) : (
                <FileViewer
                  fileType={viewerType}
                  filePath={viewerUrl}
                  onError={(e) => {
                    console.error('FileViewer error', e);
                    closePreview();
                    setTimeout(() => alert('Cannot preview this file type. It may have been downloaded instead or please try Download.'), 0);
                  }}
                />
              )}
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Controls = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  flex-wrap: wrap;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background: var(--bg-secondary);
  border-radius: 8px;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
`;

const Info = styled.div`
  opacity: 0.8;
`;

const ErrorMsg = styled.div`
  color: #e44;
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
`;

const ModalContent = styled.div`
  width: min(95vw, 1200px);
  height: min(90vh, 800px);
  background: #111;
  color: #eee;
  border-radius: 10px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.4);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  background: #1b1b1b;
  border-bottom: 1px solid rgba(255,255,255,0.08);

  button {
    background: transparent;
    border: 1px solid rgba(255,255,255,0.2);
    color: #fff;
    padding: 4px 10px;
    border-radius: 6px;
    cursor: pointer;
  }
`;

const ModalBody = styled.div`
  flex: 1;
  background: #000;
  padding: 8px;
  overflow: auto;

  /* Ensure embedded viewers fit */
  > div { height: 100%; }
`;

const ProgressContainer = styled.div`
  padding: 12px;
  background: var(--bg-secondary);
  border-radius: 8px;
  margin-bottom: 12px;
`;

const ProgressLabel = styled.div`
  font-size: 14px;
  margin-bottom: 8px;
  color: var(--text-primary);
  font-weight: 500;
`;

const ProgressBarWrapper = styled.div`
  width: 100%;
  height: 20px;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 10px;
  overflow: hidden;
  position: relative;
`;

const ProgressBarFill = styled.div`
  height: 100%;
  width: ${props => props.progress}%;
  background: linear-gradient(90deg, #4caf50, #8bc34a);
  border-radius: 10px;
  transition: width 0.3s ease;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.3),
      transparent
    );
    animation: ${props => props.progress < 100 ? 'shimmer 1.5s infinite' : 'none'};
  }
  
  @keyframes shimmer {
    0% {
      transform: translateX(-100%);
    }
    100% {
      transform: translateX(100%);
    }
  }
`;

const ProgressPercentage = styled.div`
  text-align: right;
  margin-top: 4px;
  font-size: 12px;
  color: var(--text-secondary);
`;

export default StorageBrowser;
